import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';

const THX_RED = '#B71530';

const ROLE_LABELS = { reviewer: 'Reviewer', approver: 'Approver', admin: 'Admin' };

export default function Profile() {
  const { user, login } = useAuth();

  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [msg, setMsg] = useState({ text: '', type: 'info' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: 'info' }), 4000);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await client.patch('/auth/me', { display_name: displayName });
      showMsg('Profile updated successfully');
    } catch (err) {
      showMsg(err?.response?.data?.detail || 'Update failed', 'danger');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      return showMsg('New passwords do not match', 'danger');
    }
    if (newPassword.length < 8) {
      return showMsg('New password must be at least 8 characters', 'danger');
    }
    setSavingPassword(true);
    try {
      await client.patch('/auth/me', { current_password: currentPassword, new_password: newPassword });
      showMsg('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showMsg(err?.response?.data?.detail || 'Password change failed', 'danger');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div style={{ maxWidth: '600px' }}>
      {msg.text && (
        <div className={`alert alert-${msg.type} py-2 mb-4`} style={{ fontSize: '14px' }}>
          {msg.text}
        </div>
      )}

      {/* Account info */}
      <div className="card mb-4">
        <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
          Account Information
        </div>
        <div className="card-body">
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-center mb-3">
              <div
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  backgroundColor: THX_RED,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  fontWeight: '700',
                }}
              >
                {(user?.display_name || user?.email || '?')[0].toUpperCase()}
              </div>
            </div>
            <div className="row g-2 mb-1">
              <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Email</div>
              <div className="col-8" style={{ fontSize: '13px', fontWeight: '500' }}>{user?.email}</div>
            </div>
            <div className="row g-2">
              <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Role</div>
              <div className="col-8">
                <span className="badge" style={{ backgroundColor: THX_RED, fontSize: '11px' }}>
                  {ROLE_LABELS[user?.role] || user?.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit display name */}
      <div className="card mb-4">
        <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
          Display Name
        </div>
        <div className="card-body">
          <form onSubmit={handleSaveProfile}>
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: '13px' }}>Display Name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Your full name"
              />
            </div>
            <button
              type="submit"
              className="btn btn-sm"
              style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
              disabled={savingProfile}
            >
              {savingProfile ? 'Saving...' : 'Save Name'}
            </button>
          </form>
        </div>
      </div>

      {/* Change password */}
      <div className="card">
        <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
          Change Password
        </div>
        <div className="card-body">
          <form onSubmit={handleChangePassword}>
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: '13px' }}>Current Password</label>
              <input
                type="password"
                className="form-control form-control-sm"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: '13px' }}>New Password</label>
              <input
                type="password"
                className="form-control form-control-sm"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
              <div className="form-text" style={{ fontSize: '11px' }}>Minimum 8 characters</div>
            </div>
            <div className="mb-3">
              <label className="form-label" style={{ fontSize: '13px' }}>Confirm New Password</label>
              <input
                type="password"
                className="form-control form-control-sm"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-sm"
              style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
              disabled={savingPassword}
            >
              {savingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
